import { CreditsRepository } from "../../domain/repositories/CreditsRepository";
import {
  CreditsSummaryTotalEntity,
  CreditSummaryEntity,
  RefreshResponse,
  PaginatedResponse,
  ListParams,
} from "../../domain/entities/CreditSummaryEntity";
import { RecoApiDataSource } from "../datasources/RecoApiDataSource";

export class CreditsRepositoryImpl implements CreditsRepository {
  private dataSource: RecoApiDataSource;

  constructor() {
    this.dataSource = new RecoApiDataSource();
  }

  async getCustomerCreditsSummary(
    autoRefresh?: boolean
  ): Promise<CreditsSummaryTotalEntity> {
    const response = await this.dataSource.fetchCustomerCreditsSummary(
      autoRefresh
    );

    const currencies = (response.data.currencies || []).map((curr: any) => ({
      currencyCode: curr.currency_code,
      currencySymbol: curr.currency_symbol,
      totalEntities: curr.total_entities || 0,
      totalCredits: curr.total_credits || 0,
      overdueDocuments: curr.overdue_documents || 0,
      totalOverdueAmount: parseFloat(curr.total_overdue_amount) || 0,
      outstandingDocuments: curr.outstanding_documents || 0,
      totalOutstandingAmount: parseFloat(curr.total_outstanding_amount) || 0,
    }));

    return {
      currencies,
      lastCacheRefresh: response.data.last_cache_refresh || null,
    };
  }

  async getCustomerCreditsList(
    params: ListParams
  ): Promise<PaginatedResponse<CreditSummaryEntity>> {
    const response = await this.dataSource.fetchCustomerCreditsList(params);

    const credits: CreditSummaryEntity[] = (response.data || []).map(
      (item: any) => ({
        entityType: "customer" as const,
        entityId: item.entity_id,
        entityName: item.entity_name,
        availableCredits: parseFloat(item.available_credits) || 0,
        overdueDocumentsCount: parseInt(item.overdue_documents_count) || 0,
        outstandingDocumentsCount:
          parseInt(item.outstanding_documents_count) || 0,
        paymentCount: parseInt(item.payment_count) || 0,
        overdueAmount: parseFloat(item.overdue_amount) || 0,
        outstandingAmount: parseFloat(item.outstanding_amount) || 0,
        currencyCode: item.currency_code || "INR",
        currencySymbol: item.currency_symbol || "₹",
        lastUpdated: item.last_updated,
        isStale: Boolean(item.is_stale),
      })
    );

    return {
      data: credits,
      pagination: {
        total: response.pagination?.total || 0,
        page: response.pagination?.page || 1,
        limit: response.pagination?.limit || 20,
        totalPages: response.pagination?.total_pages || 0,
        hasMore: response.pagination?.has_more || false,
      },
    };
  }

  async getCustomerOutstandingInvoices(
    params: ListParams
  ): Promise<PaginatedResponse<CreditSummaryEntity>> {
    const response = await this.dataSource.fetchCustomerOutstandingInvoices(params);

    const credits: CreditSummaryEntity[] = (response.data || []).map(
      (item: any) => ({
        entityType: "customer" as const,
        entityId: item.entity_id,
        entityName: item.entity_name,
        availableCredits: parseFloat(item.available_credits) || 0,
        overdueDocumentsCount: parseInt(item.overdue_documents_count) || 0,
        outstandingDocumentsCount:
          parseInt(item.outstanding_documents_count) || 0,
        paymentCount: parseInt(item.payment_count) || 0,
        overdueAmount: parseFloat(item.overdue_amount) || 0,
        outstandingAmount: parseFloat(item.outstanding_amount) || 0,
        currencyCode: item.currency_code || "INR",
        currencySymbol: item.currency_symbol || "₹",
        lastUpdated: item.last_updated,
        isStale: Boolean(item.is_stale),
      })
    );

    return {
      data: credits,
      pagination: {
        total: response.pagination?.total || 0,
        page: response.pagination?.page || 1,
        limit: response.pagination?.limit || 20,
        totalPages: response.pagination?.total_pages || 0,
        hasMore: response.pagination?.has_more || false,
      },
    };
  }

  async refreshCustomerCreditsCache(): Promise<RefreshResponse> {
    const response = await this.dataSource.triggerCustomerCreditsRefresh();

    return {
      success: response.success || false,
      totalProcessed: response.data?.total_processed || 0,
      totalWithCredits: response.data?.total_with_credits || 0,
      processingTime: response.data?.processing_time || 0,
    };
  }

  async getVendorCreditsSummary(
    autoRefresh?: boolean
  ): Promise<CreditsSummaryTotalEntity> {
    const response = await this.dataSource.fetchVendorCreditsSummary(
      autoRefresh
    );

    const currencies = (response.data.currencies || []).map((curr: any) => ({
      currencyCode: curr.currency_code,
      currencySymbol: curr.currency_symbol,
      totalEntities: curr.total_entities || 0,
      totalCredits: curr.total_credits || 0,
      overdueDocuments: curr.overdue_documents || 0,
      totalOverdueAmount: parseFloat(curr.total_overdue_amount) || 0,
      outstandingDocuments: curr.outstanding_documents || 0,
      totalOutstandingAmount: parseFloat(curr.total_outstanding_amount) || 0,
    }));

    return {
      currencies,
      lastCacheRefresh: response.data.last_cache_refresh || null,
    };
  }

  async getVendorCreditsList(
    params: ListParams
  ): Promise<PaginatedResponse<CreditSummaryEntity>> {
    const response = await this.dataSource.fetchVendorCreditsList(params);

    const credits: CreditSummaryEntity[] = (response.data || []).map(
      (item: any) => ({
        entityType: "vendor" as const,
        entityId: item.entity_id,
        entityName: item.entity_name,
        overdueDocumentsCount: parseInt(item.overdue_documents_count) || 0,
        outstandingDocumentsCount:
          parseInt(item.outstanding_documents_count) || 0,
        availableCredits: parseFloat(item.available_credits) || 0,
        paymentCount: parseInt(item.payment_count) || 0,
        overdueAmount: parseFloat(item.overdue_amount) || 0,
        outstandingAmount: parseFloat(item.outstanding_amount) || 0,
        currencyCode: item.currency_code || "INR",
        currencySymbol: item.currency_symbol || "₹",
        lastUpdated: item.last_updated,
        isStale: Boolean(item.is_stale),
      })
    );

    return {
      data: credits,
      pagination: {
        total: response.pagination?.total || 0,
        page: response.pagination?.page || 1,
        limit: response.pagination?.limit || 20,
        totalPages: response.pagination?.total_pages || 0,
        hasMore: response.pagination?.has_more || false,
      },
    };
  }

  async getVendorOutstandingBills(
    params: ListParams
  ): Promise<PaginatedResponse<CreditSummaryEntity>> {
    const response = await this.dataSource.fetchVendorOutstandingBills(params);

    const credits: CreditSummaryEntity[] = (response.data || []).map(
      (item: any) => ({
        entityType: "vendor" as const,
        entityId: item.entity_id,
        entityName: item.entity_name,
        overdueDocumentsCount: parseInt(item.overdue_documents_count) || 0,
        outstandingDocumentsCount:
          parseInt(item.outstanding_documents_count) || 0,
        availableCredits: parseFloat(item.available_credits) || 0,
        paymentCount: parseInt(item.payment_count) || 0,
        overdueAmount: parseFloat(item.overdue_amount) || 0,
        outstandingAmount: parseFloat(item.outstanding_amount) || 0,
        currencyCode: item.currency_code || "INR",
        currencySymbol: item.currency_symbol || "₹",
        lastUpdated: item.last_updated,
        isStale: Boolean(item.is_stale),
      })
    );

    return {
      data: credits,
      pagination: {
        total: response.pagination?.total || 0,
        page: response.pagination?.page || 1,
        limit: response.pagination?.limit || 20,
        totalPages: response.pagination?.total_pages || 0,
        hasMore: response.pagination?.has_more || false,
      },
    };
  }

  async refreshVendorCreditsCache(): Promise<RefreshResponse> {
    const response = await this.dataSource.triggerVendorCreditsRefresh();

    return {
      success: response.success || false,
      totalProcessed: response.data?.total_processed || 0,
      totalWithCredits: response.data?.total_with_credits || 0,
      processingTime: response.data?.processing_time || 0,
    };
  }
}
